import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  canInlinePreview,
  fetchAttachmentBlob,
  fetchAttachmentPreviewBuffer,
  isImageFile,
  isPdfFile,
  pdfFirstPageThumbnailDataUrl,
} from "./attachmentUtils";

function fileTypeIcon(filename, mimetype) {
  const name = String(filename || "").toLowerCase();
  const mime = String(mimetype || "").toLowerCase();

  if (isPdfFile(name, mime)) {
    return { Icon: PictureAsPdfOutlinedIcon, color: "#dc2626", bg: "rgba(220, 38, 38, 0.1)" };
  }
  if (isImageFile(name, mime)) {
    return { Icon: ImageOutlinedIcon, color: "#7c3aed", bg: "rgba(124, 58, 237, 0.1)" };
  }
  if (
    name.match(/\.xls[xm]?$/) ||
    mime.includes("spreadsheet") ||
    mime.includes("excel")
  ) {
    return { Icon: TableChartOutlinedIcon, color: "#16a34a", bg: "rgba(22, 163, 74, 0.1)" };
  }
  if (name.match(/\.docx?$/) || mime.includes("word") || mime.includes("msword")) {
    return { Icon: DescriptionOutlinedIcon, color: "#2563eb", bg: "rgba(37, 99, 235, 0.1)" };
  }
  return { Icon: InsertDriveFileOutlinedIcon, color: "#64748b", bg: "rgba(100, 116, 139, 0.1)" };
}

export default function AttachmentThumbnail({
  attachmentId,
  filename,
  mimetype,
  size = 88,
  onClick,
  sx = {},
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const visual = fileTypeIcon(filename, mimetype);
  const { Icon } = visual;
  const shouldLoadPreview = canInlinePreview(filename, mimetype);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !shouldLoadPreview || !attachmentId || thumbUrl || failed) {
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    const load = async () => {
      setLoading(true);
      try {
        if (isPdfFile(filename, mimetype)) {
          const data = await fetchAttachmentPreviewBuffer(attachmentId);
          if (cancelled) return;
          const dataUrl = await pdfFirstPageThumbnailDataUrl(data, size);
          if (cancelled) return;
          setThumbUrl(dataUrl);
          return;
        }

        const blob = await fetchAttachmentBlob(attachmentId, filename, mimetype);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setThumbUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, shouldLoadPreview, attachmentId, thumbUrl, failed, filename, mimetype, size]);

  useEffect(
    () => () => {
      if (thumbUrl?.startsWith("blob:")) URL.revokeObjectURL(thumbUrl);
    },
    [thumbUrl]
  );

  const showIcon = failed || (!thumbUrl && !loading);

  return (
    <Box
      ref={ref}
      onClick={onClick}
      sx={{
        width: size,
        height: size,
        borderRadius: "10px",
        overflow: "hidden",
        flexShrink: 0,
        bgcolor: thumbUrl ? "#fff" : visual.bg,
        border: "1px solid rgba(148, 163, 184, 0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onClick ? "pointer" : "default",
        position: "relative",
        transition: "transform 0.12s, box-shadow 0.12s",
        "&:hover": onClick
          ? {
              transform: "scale(1.03)",
              boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
            }
          : {},
        ...sx,
      }}
    >
      {loading ? (
        <CircularProgress size={22} sx={{ color: visual.color, position: "absolute" }} />
      ) : null}

      {thumbUrl ? (
        <Box
          component="img"
          src={thumbUrl}
          alt={filename || "adjunto"}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : null}

      {showIcon ? (
        <Icon
          sx={{
            fontSize: size * 0.38,
            color: visual.color,
            opacity: loading ? 0.35 : 1,
            position: thumbUrl ? "absolute" : "static",
            visibility: thumbUrl ? "hidden" : "visible",
          }}
        />
      ) : null}
    </Box>
  );
}
